import { useCallback } from 'react'

import {
  useGetWorkspaceSsoSettingsQuery,
  useGetWorkspaceSettingsQuery,
  useGetCustomerQuery,
  useGetWorkspacesQuery,
} from 'gqlTypes'

export const useWorkspaceSettings = (identifier: string) => {
  // Grab the current workspace object from user workspaces and check the user
  // role in order to prevent non-authorized fields from being requested on the
  // following queries
  const { data: workspacesData } = useGetWorkspacesQuery()
  const currentWorkspace = workspacesData?.me.workspaces.find(
    workspace => workspace.identifier === identifier
  )
  const userCanAdminister = currentWorkspace?.userRole === 'ADMIN'
  const isFinanceRole = currentWorkspace?.userRole === 'FINANCE'

  // Do not include shares for finance role
  const workspaceQuery = useGetWorkspaceSettingsQuery({
    variables: { identifier, includeShares: !isFinanceRole },
    fetchPolicy: 'cache-and-network',
  })

  const { customer } = workspaceQuery.data?.workspace || {}
  const shouldLoadWorkspaceCustomer =
    (userCanAdminister || isFinanceRole) && customer?.identifier
  const shouldLoadWorkspaceSSO = userCanAdminister && customer?.ssoShortName

  const workspaceCustomer = useGetCustomerQuery({
    variables: { customerId: customer?.identifier! },
    fetchPolicy: 'cache-and-network',
    skip: !shouldLoadWorkspaceCustomer,
  })

  const workspaceSsoConfig = useGetWorkspaceSsoSettingsQuery({
    variables: { identifier },
    fetchPolicy: 'cache-and-network',
    skip: !shouldLoadWorkspaceSSO,
  })

  const refetch = useCallback(async () => {
    shouldLoadWorkspaceCustomer && (await workspaceCustomer.refetch())
    await workspaceQuery.refetch()
  }, [shouldLoadWorkspaceCustomer, workspaceCustomer, workspaceQuery])

  const loading =
    workspaceQuery.loading ||
    workspaceCustomer.loading ||
    workspaceSsoConfig.loading

  const data = {
    workspace: workspaceQuery.data?.workspace,
    customer: workspaceCustomer.data?.customer,
    sso: workspaceSsoConfig?.data?.workspace.customer,
  }

  // We need to know updating state - i.e. a state where any of the queries is refetching data
  // while we do have data that we can already show.
  // In this case, we don't need to render big snipper for the whole page.
  const updating =
    loading &&
    !!data.workspace &&
    (shouldLoadWorkspaceCustomer ? !!data.customer : true) &&
    (shouldLoadWorkspaceSSO ? !!data.sso : true)

  const error =
    workspaceQuery.error || workspaceCustomer.error || workspaceSsoConfig.error

  return { data, loading, updating, error, refetch }
}
